import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiEdit3, FiRotateCw, FiTrash2 } from "react-icons/fi";
import { useParams, Link, useNavigate } from "react-router-dom";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

const TrashModuleTableCreate = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [moduleInfo, setModuleInfo] = useState(null);
  const [items, setItems] = useState([]);
  const [perms, setPerms] = useState([]);

  const lang = useUserLanguage();
  const { translations } = useTranslations(lang);
  const t = (key, def) => translations[key] || def;

  // 1) Modül bilgisi
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("admin_token");
      try {
        const resp = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/modules/by-slug/${slug}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setModuleInfo(resp.data);
      } catch (err) {
        console.error("Modül bilgisi alınamadı:", err);
      }
    })();
  }, [slug]);

  // 2) İzinler + çöp kutusu verisi
useEffect(() => {
    if (!moduleInfo) return;          // moduleInfo hazır değilse bekle
    (async () => {
      try {
        const token = localStorage.getItem("admin_token");
        // Buraya console.log ekle
        const trashUrl = `${process.env.REACT_APP_API_BASE_URL}/api/module-data/trash/${moduleInfo.id}`;
        console.log("[TrashModuleTableCreate] moduleInfo.id:", moduleInfo.id);
        console.log("[TrashModuleTableCreate] GET URL:", trashUrl);
  
        const [permRes, dataRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/my-permissions`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(trashUrl, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
  
        console.log("[TrashModuleTableCreate] permissions:", permRes.data.permissions);
        console.log("[TrashModuleTableCreate] trash data:", dataRes.data);
  
        setPerms(permRes.data.permissions || []);
        setItems(dataRes.data);
      } catch (err) {
        console.error("[TrashModuleTableCreate] Çöp kutusu yüklenirken hata:", err.response || err);
      }
    })();
  }, [moduleInfo]);
  

  // 3) İzin kontrolü
  const role = (localStorage.getItem("userRole") || "").toLowerCase();
  const isSuperAdmin = role === "superadmin";
  const modulePerm = perms.find(p => p.module_id === moduleInfo?.id) || {};
  const canEdit    = isSuperAdmin || modulePerm.edit_permission   || modulePerm.full_access;
  const canRestore = isSuperAdmin || modulePerm.edit_permission   || modulePerm.full_access;
  const canDestroy = isSuperAdmin || modulePerm.delete_permission || modulePerm.full_access;

  // 4) İşlem fonksiyonları
  const handleRestore = async id => {
    if (!window.confirm(t("RESTORE_CONFIRM","Geri yüklemek istediğinize emin misiniz?"))) return;
    const token = localStorage.getItem("admin_token");
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/module-data/restore/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      console.error("Geri yükleme hatası:", err);
    }
  };

  const handleDestroy = async id => {
    if (!window.confirm(t("DESTROY_CONFIRM","Kalıcı silmek istediğinize emin misiniz?"))) return;
    const token = localStorage.getItem("admin_token");
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/module-data/permanent/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      console.error("Kalıcı silme hatası:", err);
    }
  };

  // 5) Dinamik sütunlar
  const dynamicCols = useMemo(() => {
    if (!moduleInfo?.elements) return [];
    let els = [];
    try {
      els = JSON.parse(moduleInfo.elements);
    } catch (e) {
      console.error("Element JSON parse hatası:", e);
    }
    return els
      .filter(el => el.table)
      .map(el => ({
        id: `col_${el.name}`,
        header: el.label || el.name,
        accessorFn: row => {
          let obj = {};
          try {
            obj = JSON.parse(row.data);
          } catch {}
          return obj[el.name] ?? "-";
        },
        meta: { headerClassName: "text-start", className: "text-start" }
      }));
  }, [moduleInfo]);

  // 6) Tüm sütunlar
  const columns = useMemo(() => {
    const staticFirst = [{
      id: "id",
      accessorKey: "id",
      header: t("TABLE_ID_LABEL","ID"),
      meta: { headerClassName: "text-start", className: "text-start" }
    }];

    const staticSecond = [
      {
        id: "created_at",
        accessorKey: "created_at",
        header: t("TABLE_CREATED_AT","Oluşturma Tarihi"),
        meta: { headerClassName: "text-end", className: "text-end date-column" },
        cell: info => new Date(info.getValue()).toLocaleDateString("en-GB")
      },
      {
        id: "deleted_at",
        accessorKey: "deleted_at",
        header: t("TABLE_DELETED_AT","Silinme Tarihi"),
        meta: { headerClassName: "text-end", className: "text-end date-column" },
        cell: info => {
          const v = info.getValue();
          return v ? new Date(v).toLocaleDateString("en-GB") : "-";
        }
      },
      {
        id: "actions",
        accessorKey: "actions",
        header: t("TABLE_ACTIONS_LABEL","İşlemler"),
        enableSorting: false,
        meta: { headerClassName: "text-end", className: "text-end" },
        cell: info => {
          const id = info.row.original.id;
          return (
            <div className="d-flex gap-2 justify-content-end">
              {canEdit && (
                <Link
                  to={`/modules/${slug}/edit/${id}`}
                  className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
                  title={t("EDIT","Düzenle")}
                >
                  <FiEdit3/>
                </Link>
              )}
              {canRestore && (
                <button
                  className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
                  title={t("RESTORE","Geri Yükle")}
                  onClick={() => handleRestore(id)}
                >
                  <FiRotateCw/>
                </button>
              )}
              {canDestroy && (
                <button
                  className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
                  title={t("DELETE_PERMANENT","Kalıcı Sil")}
                  onClick={() => handleDestroy(id)}
                >
                  <FiTrash2/>
                </button>
              )}
            </div>
          );
        }
      }
    ];

    return [ ...staticFirst, ...dynamicCols, ...staticSecond ];
  }, [dynamicCols, t, canEdit, canRestore, canDestroy, slug]);

  if (!moduleInfo) {
    return <p>{t("LOADING","Yükleniyor...")}</p>;
  }

  return (
    <div className="table-responsive">
      <Table data={items} columns={columns} />
    </div>
  );
};

export default TrashModuleTableCreate;
